/**
 * Deep-link state carried in the URL query string.
 *
 * The page mirrors shareable UI state into query params via
 * `history.replaceState` (no SPA router — the param set IS the route):
 *
 *   ?network=testnet               network selector (mainnet = default, omitted)
 *   ?window=24h                    page-level uptime window (1h = default, omitted)
 *   ?incident=42                   open incident detail
 *   ?service=liveness-v2-rest-go   expanded service card (suite key)
 *   ?lag=v2                        open the API v2 / v3 indexer-lag modal
 *   ?lagEndpoint=/api/v2/getConsensusBlock
 *                                  open a freshness endpoint's lag modal inside
 *                                  the expanded card (requires ?service=…);
 *                                  value is the endpoint's display path
 *   ?lagView=graph                 lag modal chart view (bar = default, omitted)
 *   ?lagWindow=24h                 lag modal window (1h = default, omitted)
 *   ?bucket=liveness-v3-rest-go~2026-07-15T14:13:00.000Z
 *                                  open a service card's per-bucket results
 *                                  modal; value is `<suite key>~<bucket
 *                                  startAt>`. Live windows roll forward, so a
 *                                  link only resolves while its bucket is
 *                                  still inside the shared `?window=` (or an
 *                                  anchored `?date=` view).
 *   ?date=2026-07-14               DatePicker anchor: the page shows that
 *                                  day's historical window instead of live
 *
 * At most ONE modal deep link is honored per load, by static precedence
 * `lag > lagEndpoint > bucket` — when a higher-priority param is present the
 * losers are ignored AND scrubbed from the URL at load, so hand-crafted
 * combined URLs can't stack modals now or resurrect them after a remount.
 *
 * Parsing is lenient by design: an invalid value falls back to its default
 * instead of breaking the page — never trust the address bar. Serializers
 * return `null` for default values so canonical URLs stay minimal.
 */
import type { NetworkFilter } from './filter-network';
import type { LagWindow } from './lag-time';
import type { UptimeWindow } from './status-types';

export type UrlNetwork = 'mainnet' | 'testnet';
export type LagSurface = 'v2' | 'v3';
export type LagView = 'bar' | 'graph';

export interface BucketLink {
  /** Suite key of the service card the bucket belongs to. */
  service: string;
  /** The bucket's exact `startAt` ISO timestamp — its stable identity. */
  startAt: string;
}

export interface UrlState {
  network: UrlNetwork;
  window: UptimeWindow;
  incident: number | null;
  service: string | null;
  lag: LagSurface | null;
  lagEndpoint: string | null;
  lagView: LagView;
  lagWindow: LagWindow;
  bucket: BucketLink | null;
  /** DatePicker anchor day (`YYYY-MM-DD`), or null for the live view. */
  date: string | null;
}

export type UrlParamKey =
  | 'network'
  | 'window'
  | 'incident'
  | 'service'
  | 'lag'
  | 'lagEndpoint'
  | 'lagView'
  | 'lagWindow'
  | 'bucket'
  | 'date';

// Suite keys are backend-normalized to lowercase [a-z0-9_-] (see the backend's
// component-key.util). The dot is allowed defensively; anything else — or an
// absurd length — is treated as no deep link at all.
const SERVICE_KEY_RE = /^[a-z0-9._-]{1,100}$/;

function parseNetwork(raw: string | null): UrlNetwork {
  return raw?.toLowerCase() === 'testnet' ? 'testnet' : 'mainnet';
}

function parseWindow(raw: string | null): UptimeWindow {
  return raw === '1h' || raw === '24h' || raw === '90d' ? raw : '1h';
}

function parseIncident(raw: string | null): number | null {
  if (raw === null || raw === '') return null;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parseService(raw: string | null): string | null {
  const key = raw?.trim().toLowerCase() ?? '';
  return SERVICE_KEY_RE.test(key) ? key : null;
}

function parseLag(raw: string | null): LagSurface | null {
  return raw === 'v2' || raw === 'v3' ? raw : null;
}

// A freshness endpoint's display path, e.g. `/api/v2/getConsensusBlock` —
// URL-path alphabet only, leading slash required.
const LAG_ENDPOINT_RE = /^\/[\w./-]{1,199}$/;

function parseLagEndpoint(raw: string | null): string | null {
  const path = raw?.trim() ?? '';
  return LAG_ENDPOINT_RE.test(path) ? path : null;
}

function parseLagView(raw: string | null): LagView {
  return raw === 'graph' ? 'graph' : 'bar';
}

// `startAt` is only ever compared against bucket timestamps from the API —
// never rendered or interpreted — so validation just requires an ISO-shaped,
// parseable timestamp.
const BUCKET_START_RE = /^[\dT:.Z+-]{10,40}$/;

function parseBucket(raw: string | null): BucketLink | null {
  if (raw === null) return null;
  const sep = raw.indexOf('~');
  if (sep === -1) return null;
  const service = parseService(raw.slice(0, sep));
  const startAt = raw.slice(sep + 1);
  if (service === null) return null;
  if (!BUCKET_START_RE.test(startAt)) return null;
  if (!Number.isFinite(Date.parse(startAt))) return null;
  return { service, startAt };
}

/** Serializer for the `?bucket=` param — see the module header. */
export function bucketParam(suiteKey: string, startAt: string): string {
  return `${suiteKey}~${startAt}`;
}

const DAY_ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseDate(raw: string | null): string | null {
  if (raw === null || !DAY_ISO_RE.test(raw)) return null;
  return Number.isFinite(Date.parse(raw)) ? raw : null;
}

export function parseUrlState(search: string): UrlState {
  const params = new URLSearchParams(search);
  return {
    network: parseNetwork(params.get('network')),
    window: parseWindow(params.get('window')),
    incident: parseIncident(params.get('incident')),
    service: parseService(params.get('service')),
    lag: parseLag(params.get('lag')),
    lagEndpoint: parseLagEndpoint(params.get('lagEndpoint')),
    lagView: parseLagView(params.get('lagView')),
    lagWindow: parseWindow(params.get('lagWindow')),
    bucket: parseBucket(params.get('bucket')),
    date: parseDate(params.get('date')),
  };
}

/**
 * Apply a patch to a query string: string values are set, `null` deletes the
 * key, unrelated params pass through untouched. Pure — the browser wrapper is
 * `patchUrlParams` below.
 */
export function patchSearch(
  search: string,
  patch: Readonly<Partial<Record<UrlParamKey, string | null>>>,
): string {
  const params = new URLSearchParams(search);
  for (const key of Object.keys(patch) as UrlParamKey[]) {
    const value = patch[key];
    if (value === undefined) continue;
    if (value === null) {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  }
  return params.toString();
}

/**
 * Mirror a state change into the address bar. Always replaceState — comparing
 * the next URL against location.href was flaky (encoding/normalization
 * mismatches let some switches no-op), and replaceState with an identical URL
 * is idempotent, so unconditional calls are safe.
 */
export function patchUrlParams(
  patch: Readonly<Partial<Record<UrlParamKey, string | null>>>,
): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const next = patchSearch(url.search, patch);
  const query = next === '' ? '' : `?${next}`;
  window.history.replaceState(null, '', `${url.pathname}${query}${url.hash}`);
}

// ─── Serializers — `null` means "default, drop the param" ───────────

export function networkParam(value: NetworkFilter): string | null {
  return value === 'testnet' ? 'testnet' : null;
}

export function windowParam(value: UptimeWindow): string | null {
  return value === '1h' ? null : value;
}

export function incidentParam(value: number | null): string | null {
  return value === null ? null : String(value);
}

export function lagViewParam(value: LagView): string | null {
  return value === 'bar' ? null : value;
}

export function lagWindowParam(value: LagWindow): string | null {
  return value === '1h' ? null : value;
}
